<template>
    <span
        :class="disabled ? 'module-disabled-struct' : ''"
        :uk-tooltip="
            disabled
                ? 'title:' + $t('x.module.disabled.hint', {x: $t('store')})
                : ''
        ">
        <span :class="disabled ? 'module-disabled' : ''">
            <Card :title="$t('stores')" is-dark mode="default">
                <Description :text="$t('store.description')" />

                <div class="uk-margin-medium-top" />

                <GenericView
                    is-without-filter
                    child-width-classes="uk-child-width-1-3@m uk-child-width-1-2@s">
                    <template v-slot:listLength>
                        {{ computedList.length
                        }}{{ limit ? '/' + limit : '' }}
                        {{
                            $t(computedList.length === 1 ? 'store' : 'stores') +
                            ' ' +
                            $t('overall')
                        }}
                    </template>

                    <template v-slot:newButton>
                        <span
                            :uk-tooltip="
                                computedIsLimitReached
                                    ? 'title:' +
                                      $t('store.limit.reached', {x: limit})
                                    : ''
                            ">
                            <Button
                                :action="actionNew"
                                :is-disabled="computedIsLimitReached"
                                has-action
                                mode="primary">
                                {{ $t('x.add', {x: $t('store')}) }}
                            </Button>
                        </span>
                    </template>

                    <li
                        v-for="(store, key) in computedList"
                        :key="'store-' + key">
                        <StoreItem
                            :store="store"
                            is-square
                            @select="actionSelect"
                            @delete="actionDelete" />
                    </li>
                </GenericView>

                <div
                    v-if="computedList.length === 0"
                    class="uk-margin-small-top" />
                <div v-if="computedList.length === 0">
                    {{ $t('none.x', {x: $t('stores')}) }}
                </div>

                <Modal
                    :id="modalId"
                    :title="
                        selected
                            ? $t('x.change', {x: $t('store')})
                            : $t('x.add', {x: $t('store')})
                    ">
                    <span
                        v-for="(inputField, key) in inputFields"
                        :key="inputField.name">
                        <TextField
                            v-model="inputField.value"
                            :field-key="key"
                            :icon="inputField.icon"
                            :is-optional="inputField.optional"
                            :label="inputField.label"
                            :name="inputField.name"
                            :rules="inputField.rules"
                            :validate="validationTrigger"
                            @setValid="setValid" />

                        <div
                            v-if="key < inputFields.length - 1"
                            class="uk-margin-bottom" />
                    </span>

                    <div class="uk-margin-medium-top" />
                    <LabelWithError
                        :label="$t('picture')"
                        is-dark
                        is-optional />

                    <div
                        class="uk-position-relative store-form-struct uk-text-center">
                        <img
                            v-if="image"
                            class="store-form-item"
                            :src="getLocalFileUrl(image)"
                            uk-img />
                        <img
                            v-else-if="getImageLink"
                            class="store-form-item"
                            :src="getImageLink"
                            uk-img />
                        <div
                            v-else
                            class="uk-light uk-flex uk-flex-middle uk-flex-center store-form-empty">
                            {{ $t('no.x', {x: $t('picture')}) }}
                        </div>
                    </div>

                    <div
                        class="uk-card uk-card-body uk-padding-xsmall control secondary-with-border">
                        <div class="uk-flex-center uk-flex" uk-grid>
                            <div>
                                <span v-on:click="triggerImageDialog()">
                                    <Icon
                                        :ratio="1.3"
                                        :uk-tooltip="
                                            'title:' +
                                            $t(hasImage ? 'replace' : 'add')
                                        "
                                        class="cursor-pointer action-icon"
                                        icon="cloud-upload" />
                                </span>

                                <input
                                    id="store-image"
                                    ref="image"
                                    accept=".jpg, .jpeg, .png"
                                    class="uk-hidden"
                                    type="file"
                                    v-on:click="$refs.image.value = null"
                                    v-on:change="handleImage()" />
                            </div>

                            <div v-if="hasImage">
                                <span v-on:click="removeImage()">
                                    <Icon
                                        :ratio="1.5"
                                        :uk-tooltip="'title:' + $t('delete')"
                                        class="cursor-pointer action-icon"
                                        icon="trash" />
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="uk-margin-medium-top" />
                    <Button
                        :action="actionSave"
                        :is-loading="loading"
                        :text="$t('x.save', {x: $t('store')})"
                        has-action
                        is-large
                        is-max
                        mode="primary" />

                    <div v-if="error" class="uk-margin-medium-bottom" />
                    <ErrorMessage :error="error" />
                </Modal>

                <Modal
                    :id="modalIdDelete"
                    :title="$t('x.delete', {x: $t('store')})">
                    <div class="uk-text-center">
                        {{ $t('store.delete.confirm') }}
                    </div>

                    <div
                        class="uk-flex uk-flex-center uk-margin-medium-top"
                        uk-grid>
                        <div>
                            <Button
                                :text="$t('cancel')"
                                class="uk-modal-close"
                                is-large
                                is-max
                                is-min-width
                                mode="default" />
                        </div>
                        <div>
                            <Button
                                :action="actionDeleteConfirmed"
                                :is-loading="loadingDelete"
                                :text="$t('x.delete', {x: $t('store')})"
                                has-action
                                is-large
                                is-max
                                is-min-width
                                mode="danger" />
                        </div>
                    </div>
                </Modal>
            </Card>
        </span>
    </span>
</template>

<script>
import axios from 'axios';
import UIkit from 'uikit';
import Button from '@/components/generic/Button.vue';
import Card from '@/components/generic/Card.vue';
import Description from '@/components/Description.vue';
import ErrorMessage from '@/components/generic/ErrorMessage.vue';
import GenericView from '@/components/generic/GenericView.vue';
import Icon from '@/components/generic/Icon.vue';
import LabelWithError from '@/components/generic/LabelWithError.vue';
import Modal from '@/components/generic/Modal.vue';
import StoreItem from '@/components/forms/StoreItem.vue';
import TextField from '@/components/generic/TextField.vue';

export default {
    name: 'StoresForm',
    components: {
        Button,
        Card,
        Description,
        ErrorMessage,
        GenericView,
        Icon,
        LabelWithError,
        Modal,
        StoreItem,
        TextField,
    },
    props: {
        stores: Array,
        limit: Number,
        disabled: Boolean,
    },
    data() {
        return {
            modalId: 'modal-store',
            modalIdDelete: 'modal-store-delete',

            selected: null,
            image: null,
            imageRemoved: false,

            loading: false,
            loadingDelete: false,
            error: null,

            validationTrigger: 0,
            inputFields: [
                {
                    value: '',
                    valid: false,
                    key: 'name',
                    name: 'store-name',
                    label: 'name',
                    icon: 'tag',
                    rules: 'required|max:120',
                },
                {
                    value: '',
                    valid: false,
                    key: 'street',
                    name: 'store-street',
                    label: 'street',
                    icon: 'location',
                    rules: 'required|max:120',
                },
                {
                    value: '',
                    valid: false,
                    key: 'zip',
                    name: 'store-zip',
                    label: 'zip',
                    icon: 'location',
                    rules: 'required|max:20',
                },
                {
                    value: '',
                    valid: false,
                    key: 'city',
                    name: 'store-city',
                    label: 'city',
                    icon: 'location',
                    rules: 'required|max:120',
                },
                {
                    value: '',
                    valid: false,
                    key: 'email',
                    name: 'store-email',
                    label: 'emailAddress',
                    icon: 'mail',
                    rules: 'required|emailV2|max:320',
                },
                {
                    value: '',
                    valid: true,
                    optional: true,
                    key: 'telephone',
                    name: 'store-telephone',
                    label: 'telephone',
                    icon: 'receiver',
                    rules: 'max:80',
                },
            ],
        };
    },
    computed: {
        computedList() {
            return this.stores ? this.stores : [];
        },
        computedIsLimitReached() {
            return !!this.limit && this.computedList.length >= this.limit;
        },
        getImageLink() {
            if (this.imageRemoved || !this.selected || !this.selected.imageId) {
                return '';
            }

            return (
                this.apiUrl +
                '/stores/' +
                this.selected.id +
                '/image/' +
                this.selected.imageId.split('_').slice(1).join('_')
            );
        },
        hasImage() {
            return !!this.image || !!this.getImageLink;
        },
    },
    methods: {
        setValid(valid, fieldKey) {
            this.inputFields[fieldKey].valid = valid;
        },
        triggerImageDialog() {
            document.getElementById('store-image').click();
        },
        handleImage() {
            this.image = this.$refs.image.files[0];
            this.imageRemoved = false;
        },
        removeImage() {
            this.image = null;
            this.imageRemoved = true;
        },
        actionNew() {
            if (this.computedIsLimitReached) {
                return;
            }

            this.selected = null;
            this.fill(null);

            UIkit.modal('#' + this.modalId).show();
        },
        actionSelect(store) {
            this.selected = store;
            this.fill(store);

            UIkit.modal('#' + this.modalId).show();
        },
        actionDelete(store) {
            this.selected = store;

            UIkit.modal('#' + this.modalIdDelete).show();
        },
        fill(store) {
            this.error = null;
            this.image = null;
            this.imageRemoved = false;

            for (let i = 0, j = this.inputFields.length; i < j; i++) {
                const field = this.inputFields[i];

                field.value = store ? store[field.key] ?? '' : '';
                field.valid = !!field.optional || !!field.value;
            }
        },
        actionSave() {
            this.validationTrigger++;

            for (let i = 0, j = this.inputFields.length; i < j; i++) {
                if (!this.inputFields[i].valid) {
                    return;
                }
            }

            let store = {};
            for (let i = 0, j = this.inputFields.length; i < j; i++) {
                store[this.inputFields[i].key] = this.inputFields[i].value;
            }

            this.loading = true;

            let http = axios.create();
            const request = this.selected
                ? http.put(
                      this.apiUrl + '/manage-stores/' + this.selected.id,
                      store
                  )
                : http.post(this.apiUrl + '/manage-stores', store);

            request
                .then((response) => {
                    if (!this.image && !this.imageRemoved) {
                        this.saved();
                        return;
                    }

                    this.saveImage(response.data.id);
                })
                .catch((e) => {
                    this.checkGenericError(e);

                    this.error = e;
                    this.loading = false;
                });
        },
        saveImage(id) {
            let formData = new FormData();
            formData.append('image', this.image);

            let http = axios.create();
            http.put(this.apiUrl + '/manage-stores/' + id + '/image', formData)
                .then(() => {
                    this.saved();
                })
                .catch((e) => {
                    this.checkGenericError(e);

                    this.error = e;
                    this.loading = false;
                });
        },
        saved() {
            this.loading = false;
            this.error = null;

            UIkit.modal('#' + this.modalId).hide();

            this.$emit('updated');
        },
        actionDeleteConfirmed() {
            this.loadingDelete = true;

            let http = axios.create();
            http.delete(this.apiUrl + '/manage-stores/' + this.selected.id)
                .then(() => {
                    this.loadingDelete = false;

                    UIkit.modal('#' + this.modalIdDelete).hide();

                    this.$emit('updated');
                })
                .catch((e) => {
                    this.checkGenericError(e);

                    this.loadingDelete = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.store-form-item {
    max-width: 256px;
    max-height: 256px;

    width: 100%;
    height: 100%;
}

.store-form-struct {
    padding: 15px;
    background-color: rgba(34, 34, 34, 0.72);
    border: 1px rgba(34, 34, 34, 0.72) solid;
    transition: background-color 0.25s linear;
}

.store-form-empty {
    min-height: 96px;
}
</style>
